import type { ContractMetaData } from 'src/pages/procurer/contract/Start/schema';
import type { ReportFormData } from 'src/pages/procurer/contracts/FinalizeAwardedContract/schema/ReportFormSchema';
import type { Requirement } from 'src/pages/procurer/contracts/FinalizeAwardedContract/schema/RequirementDetailsSchema';

import { create } from 'zustand';

const defaultContractMetadata: ContractMetaData = {
  isNew: '',
  title: '',
  startDate: '',
  endDate: '',
  applicationStartDate: '',
  applicationEndDate: '',
  contractValue: 0,
  contractType: '',
  contractLeadName: '',
  contractLeadEmail: '',
  socialValueLeadName: '',
  socialValueLeadEmail: '',
  supplierName: '',
  supplierContractLeadName: '',
  supplierContractLeadEmail: '',
  supplierSocialValueLeadName: '',
  supplierSocialValueLeadEmail: '',
  commitmentPeriod: 'annual',
  procurementLeadName: '',
  procurementLeadEmail: '',
  contractExtensionYears: undefined,
  department: '',
};

interface ReportingData {
  occurrenceType: ReportFormData['occurrenceType'] | undefined;
  dayOfMonth: ReportFormData['dayOfMonth'];
  weekOrdinal: ReportFormData['weekOrdinal'];
  weekDay: ReportFormData['weekDay'];
  rrule: ReportFormData['rrule'] | undefined;
}

const requirementsDefault: Requirement[] = [];
const reportingDataDefault: ReportingData = {
  occurrenceType: undefined,
  dayOfMonth: undefined,
  weekOrdinal: undefined,
  weekDay: undefined,
  rrule: undefined,
};

export type ContractSetupStore = {
  sessionId: string;
  contractMetadata: ContractMetaData;
  requirements: Requirement[];
  reportingData: ReportingData;
  errors: Record<string, any>;
  hydrationVersion: number;
  resetSessionId: () => void;
  setContractMetadata: (data: ContractMetaData) => void;
  setRequirements: (data: Requirement[] | ((prev: Requirement[]) => Requirement[])) => void;
  setReportingData: (data: ReportingData | ((prev: ReportingData) => ReportingData)) => void;
  resetContractSetup: () => void;
  setErrors: (errors: any) => void;
  incrementHydration: () => void;
};

export const useContractSetupStore = create<ContractSetupStore>((set) => ({
  sessionId: crypto.randomUUID(),
  contractMetadata: defaultContractMetadata,
  requirements: requirementsDefault,
  reportingData: reportingDataDefault,
  errors: {},
  hydrationVersion: 0,
  resetSessionId: () => set({ sessionId: crypto.randomUUID() }),
  setContractMetadata: (data) => set({ contractMetadata: data }),
  setRequirements: (data: Requirement[] | ((prev: Requirement[]) => Requirement[])) =>
    set((state) => ({
      requirements: typeof data === 'function' ? data(state.requirements) : data,
    })),
  setReportingData: (data: ReportingData | ((prev: ReportingData) => ReportingData)) =>
    set((state) => ({
      reportingData: typeof data === 'function' ? data(state.reportingData) : data,
    })),
  resetContractSetup: () =>
    set({
      sessionId: crypto.randomUUID(),
      contractMetadata: defaultContractMetadata,
      requirements: requirementsDefault,
      reportingData: reportingDataDefault,
      errors: {},
      hydrationVersion: 0,
    }),
  setErrors: (errors) => set({ errors }),
  incrementHydration: () => set((state) => ({ hydrationVersion: state.hydrationVersion + 1 })),
}));
